/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useEffect, useState } from 'react';

import { GridColumn } from '@react/react-spectrum/Grid';
import { Accordion, AccordionItem } from '@react/react-spectrum/Accordion';
import Datepicker from '@react/react-spectrum/Datepicker';
import FieldLabel from '@react/react-spectrum/FieldLabel';
import Search from '@react/react-spectrum/Search';
import { defineMessages, useIntl } from 'react-intl';

import { useProductAdminContext } from './ProductAdminContext';
import { trackEvent } from '../../utils/tracking';
import { event_productadmin_search, event_productadmin_stagedcatalog } from '../../utils/trackingEvents';

import classes from './Sidebar.scss';

const messages = defineMessages({
    headlineSearch: {
        id: 'headline.search',
        defaultMessage: 'Search'
    },
    headlineStagedCatalog: {
        id: 'headline.stagedCatalog',
        defaultMessage: 'Staged Catalog'
    },
    labelProductSearch: {
        id: 'label.productSearch',
        defaultMessage: 'Product Search'
    },
    labelPreviewDate: {
        id: 'label.previewDate',
        defaultMessage: 'Preview Date'
    },
    placeholderSearchTerm: {
        id: 'placeholder.searchTerm',
        defaultMessage: 'Search Term'
    }
});

const Sidebar = () => {
    const [{ config, searchTerm, previewDate }, { setPreviewDate, setSearchTerm }] = useProductAdminContext();
    const { formatMessage } = useIntl();

    const [searchInput, setSearchInput] = useState(searchTerm || '');

    useEffect(() => {
        // Update component state if searchTerm in ProductAdminContext changes
        const searchTermText = searchTerm ? searchTerm : '';
        if (searchTermText != searchInput) {
            setSearchInput(searchTermText);
        }
    }, [searchTerm]);

    // Only enable search if config was selected and preview data is not set
    const searchEnabled = config !== null && !previewDate;

    // Only enable staging if config was selected and search term is not set
    const previewDateEnabled = config !== null && !searchTerm;

    return (
        <GridColumn size={1} className={classes.sidebar}>
            <Accordion multiselectable>
                <AccordionItem header={formatMessage(messages.headlineSearch)} disabled={!searchEnabled}>
                    <FieldLabel label={formatMessage(messages.labelProductSearch)}>
                        <Search
                            data-test-id="filter-search"
                            className={classes.search_field}
                            aria-label={formatMessage(messages.labelProductSearch)}
                            placeholder={formatMessage(messages.placeholderSearchTerm)}
                            disabled={!searchEnabled}
                            value={searchInput}
                            onChange={setSearchInput}
                            onSubmit={v => {
                                trackEvent(event_productadmin_search);
                                setSearchTerm(v);
                            }}
                        />
                    </FieldLabel>
                </AccordionItem>
                <AccordionItem header={formatMessage(messages.headlineStagedCatalog)} disabled={!previewDateEnabled}>
                    <FieldLabel label={formatMessage(messages.labelPreviewDate)}>
                        <Datepicker
                            data-test-id="filter-preview-date"
                            disabled={!previewDateEnabled}
                            type="datetime"
                            min="today"
                            aria-label={formatMessage(messages.labelPreviewDate)}
                            value={previewDate}
                            onChange={date => {
                                trackEvent(event_productadmin_stagedcatalog);
                                const timestamp = Date.parse(date);
                                setPreviewDate(isNaN(timestamp) ? null : date);
                            }}
                        />
                    </FieldLabel>
                </AccordionItem>
            </Accordion>
        </GridColumn>
    );
};

export default Sidebar;
